import { useEffect } from "react";

import type { ChromeVisibilityContextValue } from "@/react/components/Mobile/context/sectionTabs/chromeTypes";
import { useMobileChromeVisibility } from "@/react/components/Mobile/hooks/chrome/useMobileChromeVisibility";

/**
 * Builds a renderless registrar component that flips one boolean flag in
 * ChromeVisibilityContext to `true` on mount and restores it to `false`
 * on unmount. Shared shape behind `MobileHideChromeRegistrar`,
 * `MobileHideBottomNavRegistrar`, and `MobileDisableHideChromeOnScrollRegistrar`
 * - only the setter differs between them.
 *
 * The flags themselves are ref-counted (see `useRefCountedFlag` in
 * `context/sectionTabs/useRefCountedFlag.ts`), so it's safe for multiple
 * registrants to be mounted at the same time.
 */
export const createBooleanChromeRegistrar = (
  selectSetter: (
    context: ChromeVisibilityContextValue,
  ) => (active: boolean) => void,
  displayName?: string,
) => {
  const BooleanChromeRegistrar = () => {
    const context = useMobileChromeVisibility();
    const setActive = selectSetter(context);

    useEffect(() => {
      setActive(true);
      return () => {
        setActive(false);
      };
    }, [setActive]);

    return null;
  };

  // Without this, every registrar built by this factory shows up as the
  // same "BooleanChromeRegistrar" in React DevTools - name it after the
  // call site instead.
  if (displayName) {
    BooleanChromeRegistrar.displayName = displayName;
  }

  return BooleanChromeRegistrar;
};
